import { PartnerType } from '@/api/types.ts';
import { StripeSection } from '@/features/OnBoarding/StepPayment/StripeSection.tsx';
import { PaymentStepFormType } from '@/features/OnBoarding/StepPayment/types.ts';
import { useGetPaymentMethod } from '@/features/OnBoarding/StepPayment/useGetPaymentMethod.tsx';
import {
  Button,
  Paper,
  regexEmail,
  TextField,
  Typography,
} from '@arsnovasystems/ars-front-lib';
import AddCardRoundedIcon from '@mui/icons-material/AddCardRounded';
import AssuredWorkloadRoundedIcon from '@mui/icons-material/AssuredWorkloadRounded';
import { Box, Stack } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

/**
 * Step payment form (inner of step payment)
 *
 * @param props    props of the component
 * @param props.me the partner filling the form
 *
 * @returns        JSX of the component
 */
export const StepPaymentForm = ({ me }: { me: PartnerType }) => {
  const navigate = useNavigate();

  const { meanOfPayment } = useGetPaymentMethod();

  const { t } = useTranslation();

  const methods = useForm<PaymentStepFormType>({
    defaultValues: {
      accountingEmail: me.accountingEmail,
      companyVatNumber: me.companyVatNumber,
    },
  });

  return (
    <FormProvider {...methods}>
      <Paper
        sx={(theme) => ({
          marginTop: 4,
          width: 'stretch',
          maxWidth: '600px',
          boxShadow: theme.shadows[6],
        })}
      >
        <Stack
          direction={'row'}
          sx={{ gap: 2, alignItems: 'center' }}
        >
          <AssuredWorkloadRoundedIcon />
          <Box>
            <Typography variant={'subtitle1'}>
              {t('ik.invoicing.information')}
            </Typography>
            <Typography
              variant={'subtitle2'}
              color={'text.secondary'}
            >
              {t('ik.onboarding.stepThree.subHeaderGeneralHint')}
            </Typography>
          </Box>
        </Stack>

        <form>
          <Controller
            name={'companyVatNumber'}
            control={methods.control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => {
              return (
                <TextField
                  sx={{ marginTop: 2 }}
                  fullWidth={true}
                  error={fieldState.invalid}
                  label={t('ik.onboarding.stepThree.label.vat')}
                  {...field}
                />
              );
            }}
          />
          <Controller
            name={'accountingEmail'}
            control={methods.control}
            rules={{
              required: true,
              pattern: {
                value: regexEmail,
                message: t('Wrong email format'),
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <TextField
                  sx={{ marginTop: 2 }}
                  fullWidth={true}
                  label={t('ik.accounting.email')}
                  error={fieldState.invalid}
                  {...field}
                />
              );
            }}
          />
        </form>

        <Stack
          direction={'row'}
          sx={{ gap: 2, alignItems: 'center', mt: 4 }}
        >
          <AddCardRoundedIcon />
          <Box>
            <Typography variant={'subtitle1'}>
              {t('ik.payment.means')}
            </Typography>
            <Typography
              variant={'subtitle2'}
              color={'text.secondary'}
            >
              {t('ik.onboarding.stepThree.subHeaderStripeHint')}
            </Typography>
          </Box>
        </Stack>

        <StripeSection me={me} />

        <Stack
          direction={'row'}
          sx={{ justifyContent: 'center', gap: 3, marginTop: 3 }}
        >
          <Button
            size={'large'}
            variant={'outlined'}
            onClick={() => {
              void navigate(`/onboarding/step/branding${location.search}`);
            }}
          >
            {t('Back')}
          </Button>
          <Button
            disabled={meanOfPayment === undefined}
            size={'large'}
            onClick={() => {
              void navigate(`/onboarding/step/review${location.search}`);
            }}
          >
            {t('Next')}
          </Button>
        </Stack>
      </Paper>
    </FormProvider>
  );
};
